import {GeneralStatusEnum} from "../enums/GeneralStatusEnum";
import {UserRoleEnum} from "../enums/UserRoleEnum";
import {IApiUser} from "../services/nestjs-base-api";

export class Utils {
    public static readableGeneralStatus(status: GeneralStatusEnum) {
        switch (status) {
            case GeneralStatusEnum.Active:
                return "Active";
            case GeneralStatusEnum.Suspended:
                return "Suspended";
            case GeneralStatusEnum.Deleted:
                return "Deleted";
            default:
                return status;
        }
    }

    public static readableUserRole(role: UserRoleEnum) {
        switch (role) {
            case UserRoleEnum.OrganisationAdmin:
                return "Organisation administrator";
            case UserRoleEnum.OrganisationViewer:
                return "Organisation viewer";
            case UserRoleEnum.Admin:
                return "Platform admin";
            case UserRoleEnum.SuperAdmin:
                return "Super admin";
            case UserRoleEnum.AgencyAdmin:
                return "Agency administrator";
            case UserRoleEnum.AgencyUser:
                return "Agency user";
            default:
                return role;
        }
    }

    public static userIsSuperAdmin(user: IApiUser) {
        if (!user || !user.roles) return false;
        
        // Handle both string (JSON) and array formats
        const roles = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;

        return !!roles.includes(UserRoleEnum.SuperAdmin);
    }

    public static userIsOrganisationAdmin(user: any) {
        if (!user || !user.roles) return false;

        // Handle both string (JSON) and array formats
        const roles = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;

        // Agency users in context with MANAGER access are treated as org admins
        if (user.agencyContext?.accessLevel === 'MANAGER') return true;

        return !!roles.includes(UserRoleEnum.OrganisationAdmin);
    }

    public static userIsAdmin(user: any) {
        if (!user || !user.roles) return false;
        
        // Handle both string (JSON) and array formats
        const roles = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;
        return !!roles.includes(UserRoleEnum.Admin);
    }

    public static userIsPlatformAdmin(user: any) {
        if (!user || !user.roles) return false;

        // Handle both string (JSON) and array formats
        const roles = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;
        return !!roles.includes(UserRoleEnum.SuperAdmin) || !!roles.includes(UserRoleEnum.Admin);
    }

    public static userIsAgencyUser(user: any) {
        if (!user || !user.roles) return false;

        const roles = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;
        return !!roles.includes(UserRoleEnum.AgencyAdmin) || !!roles.includes(UserRoleEnum.AgencyUser);
    }

    public static limitAndRound(value: number, min: number, max: number, decimals: number) {
        if (value) {
            const limitedValue = (value < min) ? min : (value > max) ? max : value;

            return limitedValue.toFixed(decimals);
        } else if (value === 0) {
            const limitedValue = 0;

            return limitedValue.toFixed(decimals);
        } else {
            return '-';
        }
    }

    public static getPublicSurveyUrl(surveyHash: string): string {
        const surveyBaseUrl = process.env.REACT_APP_SURVEY_BASE_URL || `${window.location.origin}/`;
        return `${surveyBaseUrl}${surveyHash}/{customer_identifier}`;
    }

    public static getTestSurveyUrl(surveyHash: string): string {
        const surveyBaseUrl = process.env.REACT_APP_SURVEY_BASE_URL || `${window.location.origin}/`;
        return `${surveyBaseUrl}${surveyHash}/TEST_DO_NOT_RECORD_RESPONSES`;
    }

    /**
     * Copy text to the clipboard, resolving to whether it succeeded. Uses the
     * async Clipboard API when available (HTTPS / localhost) and falls back to a
     * hidden-textarea execCommand for non-secure contexts (e.g. http on a LAN
     * IP), where `navigator.clipboard` is undefined and would otherwise throw.
     */
    public static async copyText(text: string): Promise<boolean> {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch {
            // fall through to the legacy path
        }
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.top = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(textarea);
            return ok;
        } catch {
            return false;
        }
    }
}
